import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import type { CalendarView } from '../../types/calendar';

interface CalendarHeaderProps {
  currentDate: Date;
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
  onAddEvent: () => void;
  staffList?: { id: string; name: string; visible_in_public?: boolean }[];
  selectedStaffId?: string | null;
  onStaffSelect?: (id: string | null) => void;
  currentUser?: any;
  specialDates?: { date: string; note?: string }[];
}

/**
 * CalendarHeader – RTL + גלילה חודשית רגילה:
 * - ימי החודש מימין לשמאל (RTL) והגלילה בהתאם.
 * - בלי Snap/”החזרות” בזמן גלילה ידנית.
 * - מעבר לחודש אחר רק דרך בורר החודש/”היום”.
 * - הדגשה מיידית ביום שנבחר.
 * - אין "הצג הכל" בסינון צוות; תמיד איש צוות ראשון מסומן כברירת מחדל.
 */
const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  view,
  onViewChange,
  onNavigate,
  onAddEvent,
  staffList = [],
  selectedStaffId,
  onStaffSelect,
  currentUser,
  specialDates = [],
}) => {
  const nav = useNavigate();

  // ===== תפריט תצוגה =====
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // ===== Popup חודש/שנה =====
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState<number | undefined>();
  const [pickerMonth, setPickerMonth] = useState<number | undefined>();

  // ===== בסיס חודש (1 בחודש) ומצב בחירה =====
  const firstOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
  const [monthBase, setMonthBase] = useState<Date>(firstOfMonth(currentDate));
  const [selectedTemp, setSelectedTemp] = useState<Date>(new Date(currentDate)); // הדגשה מיידית

  // סנכרון currentDate "מבחוץ"
  useEffect(() => {
    setMonthBase(firstOfMonth(currentDate));
    setSelectedTemp(new Date(currentDate));
    requestAnimationFrame(() => scrollDayIntoView(currentDate, false));
  }, [currentDate]);

  // ===== ימי החודש =====
  const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const monthDays = useMemo(() => {
    const y = monthBase.getFullYear();
    const m = monthBase.getMonth();
    const count = daysInMonth(y, m);
    return Array.from({ length: count }, (_, i) => new Date(y, m, i + 1));
  }, [monthBase]);

  // ===== כלי עזר =====
  const ITEM_W = 56; // רוחב "יום" (שמור רק לשימוש נקודתי אם תרצה)
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dateKey = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const isSelectedDay = (d: Date) => isSameDay(d, selectedTemp);

  // ===== פס הימים (RTL) =====
  const dayStripRef = useRef<HTMLDivElement>(null);

  // גלילה עדינה ליום (עובד טוב גם ב-RTL)
  const scrollDayIntoView = (d: Date, smooth = true) => {
    const key = dateKey(d);
    const el = dayStripRef.current?.querySelector(
      `[data-date="${key}"]`
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        inline: 'center',
        block: 'nearest',
      });
    }
  };

  // בלי שום היגיון שנלחם בגלילה – הכל טבעי
  const onDayStripScroll: React.UIEventHandler<HTMLDivElement> = () => {
    // אין תיקונים אוטומטיים.
  };
  const onDayStripWheel: React.WheelEventHandler<HTMLDivElement> = () => {};
  const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => {};

  // ===== Popup התנהגות =====
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (ev: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  useEffect(() => {
    if (pickerOpen) {
      setPickerYear(monthBase.getFullYear());
      setPickerMonth(monthBase.getMonth());
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [pickerOpen, monthBase]);

  // ===== ניווטים =====
  const gotoToday = () => {
    const now = new Date();
    setMonthBase(firstOfMonth(now));
    setSelectedTemp(now);
    onViewChange('day');
    window.dispatchEvent(new CustomEvent('calendar-goto-today'));
    requestAnimationFrame(() => scrollDayIntoView(now));
  };

  // חיצים יום-קודם/הבא – קלאמפ בתוך החודש
  const jumpBy = (days: number) => {
    const base = new Date(selectedTemp);
    base.setDate(base.getDate() + days);

    const y = monthBase.getFullYear();
    const m = monthBase.getMonth();
    const minDate = new Date(y, m, 1);
    const maxDate = new Date(y, m, daysInMonth(y, m));

    const inMonth =
      base >= minDate && base <= maxDate ? base : base < minDate ? minDate : maxDate;

    setSelectedTemp(inMonth);
    onViewChange('day');
    window.dispatchEvent(new CustomEvent('calendar-goto-date', { detail: inMonth }));
    requestAnimationFrame(() => scrollDayIntoView(inMonth));
  };

  const applyPicker = () => {
    if (pickerYear == null || pickerMonth == null) return;
    const newBase = new Date(pickerYear, pickerMonth, 1);
    const pickDate = new Date(pickerYear, pickerMonth, 1);
    setMonthBase(newBase);
    setSelectedTemp(pickDate);
    onViewChange('day');
    window.dispatchEvent(new CustomEvent('calendar-goto-date', { detail: pickDate }));
    setPickerOpen(false);
    requestAnimationFrame(() => scrollDayIntoView(pickDate));
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setPickerYear(now.getFullYear());
    setPickerMonth(now.getMonth());
  };

  // ===== תוויות =====
  const weekDaysShort = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];
  const monthNames = [
    'ינואר',
    'פברואר',
    'מרץ',
    'אפריל',
    'מאי',
    'יוני',
    'יולי',
    'אוגוסט',
    'ספטמבר',
    'אוקטובר',
    'נובמבר',
    'דצמבר',
  ];
  const displayDate = monthBase;

  const currentYear = monthBase.getFullYear();
  const yearsRange = useMemo(
    () => Array.from({ length: 41 }, (_, i) => currentYear - 20 + i),
    [currentYear]
  );

  // ===== צוות: ברירת מחדל – הראשון תמיד מסומן =====
  const filteredStaff = useMemo(
    () => (staffList || []).filter((s) => s.visible_in_public !== false),
    [staffList]
  );

  // ודא שיש עסק (currentUser?.business_id) לפני שמנסה לבחור איש צוות
  useEffect(() => {
    if (!onStaffSelect) return;
    if (!currentUser?.business_id) return; // אין מזהה עסק, אל תבחר
    if (filteredStaff.length === 0) return;
    const firstId = filteredStaff[0].id;
    if (!selectedStaffId || !filteredStaff.some((s) => s.id === selectedStaffId)) {
      onStaffSelect(firstId);
    }
  }, [filteredStaff, selectedStaffId, onStaffSelect, currentUser?.business_id]);

  return (
    <div
      className="absolute left-0 right-0 z-50 w-full select-none"
      style={{
        top: 'calc(var(--header-height, 56px) + var(--safe-top, 0px))',
        paddingTop: 0,
      }}
      dir="rtl"
    >
      {/* שורת חודש */}
      <div
        className="mt-1 bg-white shadow-sm border-b border-gray-200 w-full"
        style={{ borderRadius: 0 }}
      >
        <div className="flex items-center justify-center gap-3 py-2 relative">
          {/* back */}
          <button
            onClick={() => nav('/dashboard')}
            className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white border border-gray-300 grid place-items-center hover:bg-gray-50 transition-colors"
            aria-label="חזרה"
            style={{ marginRight: '8px' }}
          >
            <ChevronRight size={18} className="text-gray-700" />
          </button>

          {/* יום קודם/הבא */}
          <button
            onClick={() => jumpBy(-1)}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-gray-100 transition-colors"
            aria-label="יום קודם"
          >
            <ChevronRight size={18} className="text-gray-700" />
          </button>

          {/* כותרת חודש/שנה */}
          <button
            className="text-sm font-semibold text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setPickerOpen((v) => !v)}
            aria-label="בחר חודש ושנה"
          >
            {displayDate.toLocaleDateString('he-IL', {
              month: 'long',
              year: 'numeric',
            })}
          </button>

          <button
            onClick={() => jumpBy(1)}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-gray-100 transition-colors"
            aria-label="יום הבא"
          >
            <ChevronLeft size={18} className="text-gray-700" />
          </button>
        </div>

        {/* פס ימים – חודש בלבד, RTL, גלילה טבעית */}
        <div className="px-2 pb-1">
          <div
            ref={dayStripRef}
            dir="rtl" // חשוב: התאריכים מימין לשמאל והגלילה בהתאם
            onScroll={onDayStripScroll}
            onWheel={onDayStripWheel}
            onTouchEnd={onTouchEnd}
            className="flex items-center gap-1 overflow-x-auto px-2"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <style>{`
              [dir="rtl"]::-webkit-scrollbar { display: none; }
            `}</style>

            {monthDays.map((date) => {
              const selected = isSelectedDay(date);
              const key = dateKey(date);
              const special = specialDates.find((s) => s.date === key);
              return (
                <button
                  key={key}
                  data-date={key}
                  onClick={() => {
                    setSelectedTemp(date); // הדגשה מיידית
                    onViewChange('day');
                    window.dispatchEvent(
                      new CustomEvent('calendar-goto-date', { detail: date })
                    );
                    requestAnimationFrame(() => scrollDayIntoView(date));
                  }}
                  aria-selected={selected}
                  className="relative flex flex-col items-center justify-center rounded transition"
                  style={{
                    minWidth: ITEM_W,
                    width: ITEM_W,
                    padding: '4px 6px',
                  }}
                  title={date.toLocaleDateString('he-IL')}
                >
                  {special?.note && (
                    <span className="absolute -top-2.5 h-1.5 w-1.5 rounded-full bg-red-500" />
                  )}
                  <span className="text-[10px] leading-none text-gray-600 font-medium">
                    {weekDaysShort[date.getDay()]}
                  </span>
                  <span
                    className={`text-sm leading-tight h-8 w-8 grid place-items-center rounded-full transition-all ${
                      selected
                        ? 'bg-blue-600 text-white font-bold shadow-sm'
                        : 'text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {date.getDate()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* כפתורי תצוגה/היום – ימין ; צוותים – שמאל (תמיד מישהו מסומן) */}
        <div className="px-3 pb-2 flex items-center justify-between w-full gap-2">
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="h-7 w-7 grid place-items-center rounded-full hover:bg-gray-100"
                aria-label="בחר תצוגה"
              >
                <Calendar size={16} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden z-50">
                  {(['day', 'week', 'month'] as CalendarView[]).map((v) => (
                    <button
                      key={v}
                      className={`w-full text-right px-4 py-2.5 text-sm transition-colors hover:bg-blue-50 ${
                        view === v ? 'font-semibold text-blue-600 bg-blue-50' : 'text-gray-700'
                      }`}
                      onClick={() => {
                        onViewChange(v);
                        setMenuOpen(false);
                      }}
                    >
                      {v === 'day'
                        ? 'תצוגת יום'
                        : v === 'week'
                        ? 'תצוגת שבוע'
                        : 'תצוגת חודש'}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={gotoToday}
              className="h-8 px-3 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors shadow-sm"
            >
              היום
            </button>
          </div>

          <div
            className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0"
            style={{ scrollbarWidth: 'none' }}
          >
            {(filteredStaff || []).map((s) => {
              const active = selectedStaffId === s.id || (!selectedStaffId && filteredStaff[0]?.id === s.id);
              return (
                <button
                  key={s.id}
                  title={s.name}
                  onClick={() => onStaffSelect?.(s.id)} // תמיד בוחרים (אין ביטול ל-null)
                  className={`h-8 rounded-lg text-xs font-medium whitespace-nowrap border transition-all ${
                    active
                      ? 'border-blue-600 text-blue-700 bg-blue-50 shadow-sm'
                      : 'border-gray-300 text-gray-800 hover:border-blue-400 hover:bg-gray-50 bg-white'
                  }`}
                  style={{
                    minWidth: 'max-content',
                    maxWidth: '240px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Popup חודש/שנה – מודרני */}
      {pickerOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm"
            onClick={() => setPickerOpen(false)}
          />
          <div
            className="fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-[92vw] max-w-[420px] animate-in fade-in zoom-in duration-200"
            role="dialog"
            aria-modal="true"
            dir="rtl"
          >
            {/* Header */}
            <div className="px-4 pt-4">
              <div className="flex items-center justify-between">
                <button
                  className="h-8 w-8 grid place-items-center rounded-full hover:bg-gray-100"
                  onClick={() => setPickerOpen(false)}
                  aria-label="סגור"
                >
                  ✕
                </button>
                <div className="text-center">
                  <div className="text-sm text-gray-500">בחרו חודש ושנה</div>
                  <div className="text-lg font-bold text-gray-900">
                    {typeof pickerMonth === 'number' && typeof pickerYear === 'number'
                      ? `${monthNames[pickerMonth]} ${pickerYear}`
                      : monthBase.toLocaleDateString('he-IL', {
                          month: 'long',
                          year: 'numeric',
                        })}
                  </div>
                </div>
                <div className="w-8" />
              </div>
            </div>

            {/* Year chips */}
            <div className="px-4 mt-2">
              <div className="flex items-center gap-2">
                <button
                  className="h-8 w-8 grid place-items-center rounded-full border border-gray-200 hover:bg-gray-50"
                  onClick={() => setPickerYear((y) => (y ?? currentYear) - 1)}
                  aria-label="שנה קודמת"
                >
                  <ChevronRight size={16} />
                </button>
                <div className="flex-1 overflow-x-auto no-scrollbar">
                  <div className="flex gap-2 px-1 py-1">
                    {yearsRange.map((y) => {
                      const active = y === (pickerYear ?? currentYear);
                      return (
                        <button
                          key={y}
                          onClick={() => setPickerYear(y)}
                          className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border transition-all ${
                            active
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400 hover:bg-gray-50'
                          }`}
                        >
                          {y}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button
                  className="h-8 w-8 grid place-items-center rounded-full border border-gray-200 hover:bg-gray-50"
                  onClick={() => setPickerYear((y) => (y ?? currentYear) + 1)}
                  aria-label="שנה הבאה"
                >
                  <ChevronLeft size={16} />
                </button>
              </div>
            </div>

            {/* Months grid */}
            <div className="px-4 py-3">
              <div className="grid grid-cols-3 gap-2">
                {monthNames.map((label, idx) => {
                  const isSelected = idx === (pickerMonth ?? monthBase.getMonth());
                  const isCurrentMonth =
                    idx === new Date().getMonth() &&
                    (pickerYear ?? currentYear) === new Date().getFullYear();
                  return (
                    <button
                      key={label}
                      onClick={() => setPickerMonth(idx)}
                      className={`h-12 rounded-xl border text-sm font-medium transition-all ${
                        isSelected
                          ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-blue-400 hover:bg-gray-50'
                      }`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {label}
                        {isCurrentMonth && (
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-500" />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between mt-3">
                <div className="flex gap-2">
                  <button
                    onClick={gotoToday}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-800 text-sm hover:bg-gray-200 transition-colors"
                  >
                    היום
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-1.5 rounded-lg bg-gray-100 text-gray-800 text-sm hover:bg-gray-200 transition-colors"
                    onClick={() => setPickerOpen(false)}
                  >
                    סגור
                  </button>
                  <button
                    className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                    onClick={applyPicker}
                  >
                    עבור
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarHeader;
